import * as React from 'react'

import { Loader } from '~/components/loader/Loader'
import { styled } from '~/styled'
import { NavigatorActions } from '~/types/navigatorActions.types'
import { Override } from '~/utilities/types'
import { getExternalAnchorProps } from '~/utilities/uri'

export const StyledButton = styled(
  'button',
  {
    base: [
      'items-center',
      'bg-[unset]',
      'rounded-md',
      'cursor-pointer',
      'flex',
      'font-body',
      'font-semibold',
      'justify-center',
      'no-underline',
      'transition-all',
      'duration-100',
      'ease-out',
      'whitespace-nowrap',
      'w-max',
      'disabled:cursor-not-allowed',
      'disabled:opacity-30'
    ],
    variants: {
      theme: {
        primary: [
          '[--base:var(--color-primary-800)]',
          '[--interact:var(--color-primary-900)]',
          '[--active:var(--color-primary-1000)]'
        ],
        secondary: [
          '[--base:var(--color-primary-1000)]',
          '[--interact:var(--color-primary-1100)]',
          '[--active:var(--color-primary-1200)]'
        ],
        success: [
          '[--base:var(--color-success)]',
          '[--interact:var(--color-success-mid)]',
          '[--active:var(--color-success-dark)]'
        ],
        warning: [
          '[--base:var(--color-warning)]',
          '[--interact:var(--color-warning-mid)]',
          '[--active:var(--color-warning-dark)]',
          '[--text:var(--color-grey-800)]'
        ],
        danger: [
          '[--base:var(--color-danger)]',
          '[--interact:var(--color-danger-mid)]',
          '[--active:var(--color-danger-dark)]'
        ],
        neutral: [
          '[--base:white]',
          '[--interact:rgba(255,255,255,0.9)]',
          '[--active:rgba(255,255,255,0.75)]',
          '[--text:var(--color-primary-800)]'
        ]
      },
      appearance: {
        solid: [
          'bg-(--base)',
          'text-(--text,white)',
          'disabled:opacity-30',
          'disabled:cursor-not-allowed',
          'not-disabled:hover:bg-(--interact)',
          'not-disabled:hover:text-(--text,white)',
          'not-disabled:focus:bg-(--interact)',
          'not-disabled:focus:text-(--text,white)',
          'not-disabled:active:bg-(--active)'
        ],
        outline: [
          'border',
          'border-current',
          'text-(--base)',
          'disabled:opacity-30',
          'disabled:cursor-not-allowed',
          'not-disabled:hover:no-underline',
          'not-disabled:hover:text-(--interact)',
          'not-disabled:focus:no-underline',
          'not-disabled:focus:text-(--interact)',
          'not-disabled:active:text-(--active)'
        ]
      },
      size: {
        sm: [
          'text-sm',
          'leading-[1.53]',
          'h-8',
          'px-4',
          'gap-2',
          '[&_svg]:size-4'
        ],
        md: [
          'text-md',
          'leading-normal',
          'h-10',
          'px-8',
          'gap-3',
          '[&_svg]:size-5'
        ],
        lg: [
          'text-lg',
          'leading-normal',
          'h-12',
          'px-8',
          'gap-3',
          '[&_svg]:size-[22px]'
        ],
        xl: [
          'text-lg',
          'leading-normal',
          'h-16',
          'px-8',
          'gap-3',
          '[&_svg]:size-[22px]'
        ]
      },
      isLoading: {
        true: ['cursor-not-allowed opacity-60 pointer-events-none']
      },
      fullWidth: {
        true: ['w-full'],
        false: ['w-max']
      }
    },
    defaultVariants: {
      appearance: 'solid',
      size: 'md',
      theme: 'primary'
    }
  },
  {
    enabledResponsiveVariants: true
  }
)

const LoaderContentsWrapper = styled(
  'span',
  {
    base: ['items-center', 'flex', 'justify-center', 'invisible'],
    variants: {
      size: {
        sm: ['gap-2'],
        md: ['gap-3'],
        lg: ['gap-3'],
        xl: ['gap-3']
      }
    },
    defaultVariants: {
      size: 'md'
    }
  },
  {
    enabledResponsiveVariants: true
  }
)

const WithLoader = ({
  size,
  children
}: React.ComponentProps<typeof LoaderContentsWrapper>) => (
  <>
    <Loader className="absolute" />
    <LoaderContentsWrapper size={size}>{children}</LoaderContentsWrapper>
  </>
)

type ButtonProps = Override<
  React.ComponentProps<typeof StyledButton>,
  {
    children: React.ReactNode
    href?: string
    isLoading?: boolean
  } & NavigatorActions
>

export const Button = React.forwardRef<HTMLElement, ButtonProps>(
  (
    { children, as, href, isLoading = false, onClick, disabled, ...rest },
    ref
  ) => (
    <StyledButton
      as={as || (href ? 'a' : undefined)}
      {...(!disabled && !isLoading && { href, onClick })}
      isLoading={isLoading}
      type={!href ? 'button' : undefined}
      disabled={disabled}
      {...rest}
      {...getExternalAnchorProps(href)}
      ref={ref as any}
    >
      {isLoading ? (
        <WithLoader size={rest.size}>{children}</WithLoader>
      ) : (
        children
      )}
    </StyledButton>
  )
)

Button.displayName = 'Button'
